export interface DoorCodeParseResult {
  /**
   * The alpha numeric portion of the door code.
   */
  code: string
  /**
   * The non-alpha numeric special character at the beginning/end of the code.
   * If there is no special character, this will be undefined.
   */
  char: string | undefined
  /**
   * Where in the door code the special character was found, if at all.
   */
  position: 'start' | 'end' | undefined
}

/**
 * Parse a door code with an alpha numeric code which may have a special
 * character prefix or suffix into a structured object. The result gives you the
 * alpha numeric code, the special character (if any), and whether it was at the
 * start or end of the code.
 */
export function parseDoorCodeWithSpecialChar(
  input: string
): DoorCodeParseResult {
  const isAlphanumeric = (char: string) => /^[a-zA-Z0-9]$/.test(char)
  let result: DoorCodeParseResult = {
    code: input,
    char: undefined,
    position: undefined,
  }

  if (input.length === 0) {
    return result
  }

  if (!isAlphanumeric(input[0])) {
    result.char = input[0]
    result.code = input.slice(1)
    result.position = 'start'
  } else if (!isAlphanumeric(input[input.length - 1])) {
    result.char = input[input.length - 1]
    result.code = input.slice(0, -1)
    result.position = 'end'
  }

  return result
}

/**
 * Parse a door code with an alphanumeric code which may have an emoji (or special
 * character) prefix or suffix into a structured object. The result gives you the
 * alphanumeric code, the emoji or special character (if any), and whether it was at the
 * start or end of the code.
 */
export function parseDoorCode(str: string): DoorCodeParseResult {
  let result: DoorCodeParseResult = {
    code: str,
    char: undefined,
    position: undefined,
  }

  // Regex to match some of the more common Unicode emoji sequences...
  // See: https://regex101.com/r/0anB6Z/1
  // Note: May want to use a library instead where you can be sure of the subset of emoji we can parse.
  const emojiRegex =
    /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/

  let startEmoji = null
  let endEmoji = null
  let alphanumericPart = str

  // Check and extract emoji from the start
  const startMatch = str.match(new RegExp(`^${emojiRegex.source}`))
  if (startMatch) {
    startEmoji = startMatch[0]
    alphanumericPart = alphanumericPart.slice(startEmoji.length)
  }

  // Check and extract emoji from the end
  const endMatch = alphanumericPart.match(new RegExp(`${emojiRegex.source}$`))
  if (endMatch) {
    endEmoji = endMatch[0]
    alphanumericPart = alphanumericPart.slice(0, -endEmoji.length)
  }

  // Trim any remaining whitespace from the alphanumeric part
  const alphanumeric = alphanumericPart.trim()

  if (startEmoji) {
    result.char = startEmoji
    result.code = alphanumeric
    result.position = 'start'
  } else if (endEmoji) {
    result.char = endEmoji
    result.code = alphanumeric
    result.position = 'end'
  } else {
    result = parseDoorCodeWithSpecialChar(str)
  }
  return result
}
